import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// PUT the chosen file to the presigned S3 URL, then tell the server it landed so it can
// fingerprint it and send the document out. Returns the finalize response.
export const uploadThenFinalize = async ({ url, documentId, file, finalize }) => {
    await axios.put(url, file, { headers: { "Content-Type": file.type || "application/octet-stream" } });
    const res = await finalize({ variables: { documentId } });
    return res?.data?.finalizeDocumentUpload?.response;
};

// "Sent to 12 officers" style summary of an assign/reconcile result.
export const describeAssignment = (result) => {
    if (!result) return "Saved";
    const sent = (result.assigned || 0) + (result.reopened || 0);
    const parts = [];
    if (sent > 0) parts.push(`sent to ${sent} officer${sent === 1 ? "" : "s"}`);
    if (result.revoked > 0) parts.push(`withdrawn from ${result.revoked}`);
    if (!parts.length) return "Saved - no officers to send it to yet";
    const text = parts.join(", ");
    return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatDateTime = (value) => {
    if (!value) return "--";
    // share timestamps written as "DD/MM/YYYY HH:mm:ss" (UTC) by the older code paths
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
        const [date, time = ""] = value.split(" ");
        const [d, m, y] = date.split("/");
        const parsed = dayjs.utc(`${y}-${m}-${d}T${time || "00:00:00"}Z`);
        return parsed.isValid() ? parsed.local().format("DD/MM/YYYY HH:mm") : value;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "--";
};

export const formatDate = (value) => {
    if (!value) return "--";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "--";
};

export const STATUS_META = {
    acknowledged: { label: "Signed", color: "green" },
    pending: { label: "Outstanding", color: "blue" },
    overdue: { label: "Overdue", color: "red" },
    revoked: { label: "Withdrawn", color: "default" },
};

export const REVOKED_REASON = {
    superseded: "replaced by a newer version",
    unallocated: "no longer at the site",
    disabled: "acknowledgement switched off",
    retired: "document archived",
};

export const METHOD_LABEL = { signature: "Drawn signature", tick: "Tick box" };

// Audience in the Posts visibility shape, summarised for display.
export const AUDIENCE_TYPES = [
    { key: "employee", label: "Employees", visibility: "Employee" },
    { key: "site", label: "Sites", visibility: "Site" },
    { key: "sitegroup", label: "Site Groups", visibility: "SiteGroup" },
    { key: "customer", label: "Customers", visibility: "Customer" },
    { key: "branch", label: "Branches", visibility: "Branch" },
    { key: "supplier", label: "Suppliers", visibility: "Agency" },
];

export const emptyAudience = () => AUDIENCE_TYPES.reduce((acc, type) => ({ ...acc, [type.key]: { includeAll: false, include: [] } }), {});

export const summariseAudience = (audience) => {
    if (!audience) return "No one selected";
    const parts = AUDIENCE_TYPES
        .filter((type) => audience?.[type.key]?.includeAll || audience?.[type.key]?.include?.length > 0)
        .map((type) => audience[type.key].includeAll ? `All ${type.label.toLowerCase()}` : `${audience[type.key].include.length} ${audience[type.key].include.length === 1 ? type.label.slice(0, -1).toLowerCase() : type.label.toLowerCase()}`);
    return parts.length ? parts.join(" + ") : "No one selected";
};

export const hasAudience = (audience) => AUDIENCE_TYPES.some((type) => audience?.[type.key]?.includeAll || audience?.[type.key]?.include?.length > 0);

// Run an audit-pack export and hand the file to the browser.
export const runAuditExport = async ({ exportAudit, input, notify }) => {
    try {
        const res = await exportAudit({ variables: { input } });
        const out = res?.data?.exportComplianceAudit?.response;
        if (out?.url) window.open(out.url, "_blank");
        notify(`${out?.fileName || "Audit pack"} is downloading`);
    } catch (err) {
        notify(err.message, true);
    }
};

export const RECURRENCE_LABEL = { none: "One-off", weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly" };
